<template>
  <div class="score-sheet">
    <!-- 頂部信息 -->
    <div class="header">
      <div class="title-section">
        <h1 class="title">評分表</h1>
        <div class="title-divider"></div>
      </div>
      <div class="exam-info">
        <div class="info-container">
          <div class="info-row">
            <div class="info-item">
              <span class="info-label">分數</span>
              <div v-if="isEditing" class="d-flex align-center">
                <v-text-field
                  v-model.number="editedData.examScore"
                  type="number"
                  min="0"
                  max="100"
                  outlined
                  dense
                  hide-details
                  class="score-input"
                  @input="validateScore"
                ></v-text-field>
              </div>
              <span v-else class="info-value">{{ examData.examScore || 0 }}</span>
            </div>
            <div class="info-divider"></div>
            <div class="info-item">
              <span class="info-label">測驗時間</span>
              <span class="info-value">{{ Math.floor((examData.examDuration || 0) / 60) }}分鐘</span>
            </div>
          </div>
          <div class="info-row">
            <div class="info-item">
              <span class="info-label">測驗項目</span>
              <span class="info-value">{{ examData.exam?.name || '練習測驗' }}</span>
            </div>
            <div class="info-divider"></div>
            <div class="info-item">
              <span class="info-label">測驗考生</span>
              <span class="info-value">{{ examData.student?.name || '' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 評分等級表格 -->
    <table class="grade-table">
      <tr>
        <th colspan="5" class="grade-header">您認為考生整體表現如何</th>
      </tr>
      <tr>
        <td>不及格</td>
        <td>及格邊緣</td>
        <td>及格</td>
        <td>良好</td>
        <td>優秀</td>
      </tr>
      <tr>
        <td v-for="grade in 5" :key="`grade-${grade}`" class="grade-cell">
          <template v-if="isEditing">
            <v-radio-group v-model="editedData.examResult.result" row dense hide-details class="grade-radio-group">
              <v-radio :value="grade" class="grade-radio"></v-radio>
            </v-radio-group>
          </template>
          <template v-else>
            {{ examData.examResult?.result === grade ? '✓' : '' }}
          </template>
        </td>
      </tr>
    </table>

    <!-- 評語 -->
    <div class="comment-box">
      <template v-if="isEditing">
        <v-textarea
          v-model="editedData.examResult.comment"
          outlined
          hide-details
          rows="3"
          placeholder="請輸入評語"
        ></v-textarea>
      </template>
      <template v-else>
        {{ examData.examResult?.comment || '' }}
      </template>
    </div>

    <!-- 評分者 -->
    <div class="examiner">
      評分考官：{{ examData.teacher?.name || '考官' }}
    </div>

    <!-- 評分項目表格 -->
    <template v-for="(section, sIndex) in editedData.examResultDetail">
      <div :key="'section-' + sIndex" class="section-container">
        <div class="section-title">{{ section.SectionTitle }}</div>
        <table class="score-table" v-if="Array.isArray(section.items)">
          <thead>
            <tr>
              <th width="80">編號</th>
              <th>評分項目</th>
              <th width="100">未達標準</th>
              <th width="100">符合標準</th>
              <th width="100">超越標準</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(item, iIndex) in section.items">
              <tr :key="`item-${sIndex}-${iIndex}`">
                <td class="center">{{ String(sIndex + 1).padStart(2, '0') }}.{{ String(iIndex + 1).padStart(2, '0') }}</td>
                <td>{{ item.title }}</td>
                <template v-if="isEditing">
                  <td colspan="3">
                    <v-radio-group v-model="item.result" row dense hide-details class="ma-0 score-radio-group">
                      <v-radio :value="1" class="score-radio"></v-radio>
                      <v-radio :value="2" class="score-radio"></v-radio>
                      <v-radio :value="3" class="score-radio"></v-radio>
                    </v-radio-group>
                  </td>
                </template>
                <template v-else>
                  <td class="center">{{ isScoreOne(item.result) ? '✓' : '' }}</td>
                  <td class="center">{{ isScoreTwo(item.result) ? '✓' : '' }}</td>
                  <td class="center">{{ isScoreThree(item.result) ? '✓' : '' }}</td>
                </template>
              </tr>
              <tr v-if="isEditing || item.comment" :key="`comment-${sIndex}-${iIndex}`" class="comment-row">
                <td colspan="5" class="comment-cell">
                  <template v-if="isEditing">
                    <v-text-field
                      v-model="item.comment"
                      outlined
                      dense
                      hide-details
                      placeholder="請輸入註解"
                    ></v-text-field>
                  </template>
                  <template v-else>
                    註解：{{ item.comment }}
                  </template>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </template>

    <!-- 底部按鈕 -->
    <div class="actions">
      <v-btn 
        color="primary" 
        v-if="!isEditing" 
        @click="toggleEdit" 
        class="mr-2"
      >
        編輯評分
      </v-btn>
      <v-btn 
        v-if="isEditing" 
        color="success" 
        @click="saveChanges" 
        class="mr-2"
      >
        儲存修改
      </v-btn>
      <v-btn 
        color="black" 
        dark 
        @click="exportCSV" 
        class="mr-2"
      >
        匯出 CSV
      </v-btn>
      <v-btn 
        outlined 
        @click="close"
      >
        關閉
      </v-btn>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EditableScoreSheet',
  props: {
    examData: {
      type: Object,
      required: true,
      default: () => ({
        exam: {
          name: '', 
          duration: 0
        },
        student: {
          name: ''
        },
        examResult: {
          result: 0,
          comment: ''
        },
        teacher: {
          name: ''
        },
        examResultDetail: []
      })
    },
  },
  data() {
    return {
      isEditing: false,
      editedData: null
    }
  },
  created() {
    // 深拷貝原始數據
    this.editedData = JSON.parse(JSON.stringify(this.examData))
  },
  methods: {
    validateScore(value) {
      // 確保分數在 0-100 之間
      if (value < 0) this.editedData.examScore = 0
      if (value > 100) this.editedData.examScore = 100
    },
    isScoreOne(result) {
      if (typeof result === 'string') {
        return result === '未達標準' || result === '1'
      }
      return result === 1
    },
    isScoreTwo(result) {
      if (typeof result === 'string') {
        return result === '符合標準' || result === '2'
      }
      return result === 2
    },
    isScoreThree(result) {
      if (typeof result === 'string') {
        return result === '超越標準' || result === '3'
      }
      return result === 3
    },
    getScoreText(result) {
      if (this.isScoreOne(result)) return '未達標準';
      if (this.isScoreTwo(result)) return '符合標準';
      if (this.isScoreThree(result)) return '超越標準';
      return '';
    },
    getOverallScoreText(score) {
      const scoreMap = {
        1: '不及格',
        2: '及格邊緣',
        3: '及格',
        4: '良好',
        5: '優秀'
      };
      return scoreMap[score] || '';
    },
    async toggleEdit() {
      if (this.isEditing) {
        // 如果是從編輯模式切換回檢視模式，先儲存
        await this.saveChanges()
      }
      this.isEditing = !this.isEditing
      if (this.isEditing) {
        // 重新深拷貝數據
        this.editedData = JSON.parse(JSON.stringify(this.examData))
      }
    },
    async saveChanges() {
      try {
        // 確保 _id 存在
        if (!this.editedData._id) {
          this.editedData._id = this.examData._id
        }
        // 發出更新事件，讓父元件處理儲存邏輯
        await this.$emit('save', this.editedData)
        this.isEditing = false  // 儲存成功後關閉編輯模式
      } catch (error) {
        console.error('儲存失敗:', error)
      }
    },
    exportCSV() {
      // 準備基本資訊
      const headers = [
        '測驗項目',
        '學生姓名',
        '總分',
        '測驗時間(分鐘)',
        '整體評級',
        '評語',
        '評分考官'
      ];

      let csvContent = headers.join(',') + '\n';

      // 添加基本資訊
      const basicInfo = [
        this.examData.exam?.name || '練習測驗',
        this.examData.student?.name || '',
        this.examData.examScore || 0,
        Math.floor((this.examData.examDuration || 0) / 60),
        this.getOverallScoreText(this.examData.examResult?.result),
        this.examData.examResult?.comment || '',
        this.examData.teacher?.name || '考官'
      ].map(field => `"${field}"`).join(',');

      csvContent += basicInfo + '\n\n';

      // 添加評分項目詳細資訊
      csvContent += '"評分項目詳細資訊"\n';
      csvContent += '"項目編號","評分項目","評分結果","註解"\n';

      if (Array.isArray(this.examData.examResultDetail)) {
        this.examData.examResultDetail.forEach((section, sIndex) => {
          if (Array.isArray(section.items)) {
            section.items.forEach((item, iIndex) => {
              const itemNumber = `${String(sIndex + 1).padStart(2, '0')}.${String(iIndex + 1).padStart(2, '0')}`;
              const result = this.getScoreText(item.result);
              const row = [
                itemNumber,
                item.title || '',
                result,
                item.comment || ''
              ].map(field => `"${field}"`).join(',');
              csvContent += row + '\n';
            });
          }
        });
      }

      // 添加 BOM 以確保 Excel 正確顯示中文
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fileName = `評分表_${this.examData.student?.name || '未命名'}_${new Date().toLocaleDateString()}.csv`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    close() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.score-sheet {
  padding: 32px;
  background: white;
  max-width: 1200px;
  margin: 0 auto;
}

.header {
  margin-bottom: 32px;
}

.title-section {
  margin-bottom: 24px;
  text-align: center;
  position: relative;
}

.title {
  font-size: 32px;
  font-weight: bold;
  color: #000;
  margin-bottom: 16px;
  letter-spacing: 2px;
}

.title-divider {
  width: 60px;
  height: 4px;
  background: #000;
  margin: 0 auto;
}

.exam-info {
  background: #f8f8f8;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 32px;
}

.info-container {
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
}

.info-row:not(:last-child) {
  border-bottom: 1px solid #f0f0f0;
}

.info-item {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
}

.info-divider {
  width: 1px;
  height: 24px;
  background: #f0f0f0;
}

.info-label {
  font-size: 14px;
  color: #666;
  margin-right: 12px;
}

.info-value {
  font-size: 16px;
  font-weight: 600;
  color: #000;
}

.score-input {
  width: 80px;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

th, td {
  border: 1px solid #000;
  padding: 8px;
}

.grade-header {
  background: #000;
  color: white;
  text-align: center;
  padding: 10px;
}

.grade-cell {
  text-align: center;
  height: 40px;
  position: relative;
}

.center {
  text-align: center;
}

.comment-box {
  border: 1px solid #000;
  padding: 15px;
  margin: 20px 0;
  min-height: 80px;
}

.examiner {
  margin: 20px 0;
  font-weight: bold;
}

.section-container {
  margin-bottom: 30px;
}

.section-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 10px;
  padding: 8px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.comment-row {
  background: #f5f5f5;
}

.comment-cell {
  padding: 10px;
}

.actions {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 30px;
}

.grade-radio-group {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  margin: 0 !important;
  padding: 0 !important;
}

.grade-radio-group :deep(.v-radio) {
  margin: 0;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.grade-radio-group :deep(.v-radio .v-label) {
  display: none;
}

.grade-radio-group :deep(.v-input--selection-controls__input) {
  margin: 0;
  transform: scale(1.5);
}

.score-radio-group {
  display: flex;
  justify-content: space-around;
  width: 100%;
}

.score-radio-group :deep(.v-radio) {
  margin: 0;
  padding: 0;
  flex: 1;
  display: flex;
  justify-content: center;
}

.score-radio-group :deep(.v-radio .v-label) {
  display: none;
}

.score-radio-group :deep(.v-input--selection-controls__input) {
  margin: 0;
}
</style> 